import { AlertTriangle, ExternalLink, ScrollText, Send, Store } from 'lucide-react';
import { EntityListCard, type EntityListCardBadge } from '@/shared/components/entity-list-card';
import { ListCardActions } from '@/shared/components/list-card-actions';
import { DropdownMenuItem } from '@/shared/ui/dropdown-menu';
import { Badge } from '@/shared/ui/badge';
import { channelLabel, type AgendaCampaignRow } from '../model/group-by-client';

const FREQ_LABEL: Record<string, string> = {
  '1': 'Diário',
  '7': 'Semanal',
  '30': 'Mensal',
  '31': 'Único',
};

interface AgendaCampaignCardProps {
  campaign: AgendaCampaignRow;
  index: number;
  showPassbookBadge?: boolean;
  onInspect: () => void;
  /** Abre o log de disparos já filtrado nesta campanha. */
  onRuns?: () => void;
}

export function AgendaCampaignCard({
  campaign,
  index,
  showPassbookBadge = false,
  onInspect,
  onRuns,
}: AgendaCampaignCardProps) {
  const channel = channelLabel(campaign.tipo_disparo);
  const freq = FREQ_LABEL[String(campaign.frequencia ?? '')] ?? campaign.frequencia ?? '—';

  const badges: EntityListCardBadge[] = [
    ...(channel
      ? [
          {
            key: 'channel',
            label: channel,
            icon: Send,
            variant: 'accent' as const,
            copyLines: [{ label: 'Canal', value: channel }],
          },
        ]
      : []),
    {
      key: 'freq',
      label: freq,
      variant: 'info',
      copyLines: [{ label: 'Frequência', value: freq }],
    },
    {
      key: 'status',
      label: campaign.active === false ? 'Inativa' : 'Ativa',
      variant: campaign.active === false ? 'destructive' : 'success',
      size: 'sm',
      copyLines: [
        {
          label: 'Status',
          value: campaign.active === false ? 'Inativa' : 'Ativa',
        },
      ],
    },
    ...(campaign.ultimo_status === 'error'
      ? [
          {
            key: 'erro',
            label: 'Último disparo com erro',
            icon: AlertTriangle,
            variant: 'destructive' as const,
            wide: true,
            copyLines: [
              { label: 'Status', value: 'Erro no último disparo' },
              ...(campaign.ultimo_erro ? [{ label: 'Erro', value: campaign.ultimo_erro }] : []),
            ],
          },
        ]
      : []),
    ...(showPassbookBadge && campaign.passbookId
      ? [
          {
            key: 'loja',
            label: campaign.passbookName || `Loja ${campaign.passbookId.slice(-6)}`,
            icon: Store,
            variant: 'secondary' as const,
            wide: true,
            copyLines: [
              { label: 'Passbook', value: campaign.passbookId },
              ...(campaign.passbookName
                ? [{ label: 'Loja', value: campaign.passbookName }]
                : []),
            ],
          },
        ]
      : []),
  ];

  return (
    <EntityListCard
      selected={false}
      onToggle={() => {}}
      index={index}
      selectable={false}
      ariaLabel={`Campanha ${campaign.name ?? ''}`}
      title={campaign.name || 'Campanha'}
      subtitle={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
          <span>{campaign.nextRunLabel}</span>
          {campaign.dispatchLabel ? (
            <Badge variant="outline" className="font-normal">
              {campaign.dispatchLabel}
            </Badge>
          ) : null}
        </span>
      }
      thumbnail={
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
          <Send className="size-5" />
        </span>
      }
      badges={badges}
      badgesBelow
      actions={
        <ListCardActions
          viewLabel="Abrir no cliente"
          viewIcon={ExternalLink}
          onView={(e) => {
            e.stopPropagation();
            onInspect();
          }}
          menuChildren={
            <>
              {onRuns ? (
                <DropdownMenuItem onSelect={onRuns}>
                  <ScrollText />
                  Ver disparos
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onSelect={onInspect}>
                <ExternalLink />
                Abrir campanhas do cliente
              </DropdownMenuItem>
            </>
          }
        />
      }
    />
  );
}
